import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { api, type StripeLog } from '@/api/client'
import { useProperties } from '@/hooks/useApi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DEFAULT_TIME_RANGE,
  isDefaultTimeRange,
  resolveTimeRange,
  TimeRangePicker,
  type TimeRange,
} from '@/components/ui/time-range-picker'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { formatLocaleDateTime, formatCurrency, getPropertyDisplayName } from '@/lib/format-utils'

// Stripe reports these currencies in whole units, not minor ones — dividing by
// 100 would render a JPY amount 100x too small.
// https://docs.stripe.com/currencies#zero-decimal
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
])

/**
 * Format a logged amount in its own currency. The feed this replaced formatted
 * per event, so assuming USD here would mislabel a non-USD charge.
 */
function formatLogAmount(amount: number | null, currency: string | null): string {
  if (amount == null) return '—'
  const code = (currency || 'usd').toLowerCase()
  const value = ZERO_DECIMAL_CURRENCIES.has(code) ? amount : amount / 100
  return formatCurrency(value, code.toUpperCase())
}

/**
 * Human labels for the machine verbs the backend records. Anything unmapped
 * falls through to the raw value rather than being hidden, so a newly added
 * action still shows up instead of silently rendering blank.
 */
const ACTION_LABELS: Record<string, string> = {
  authorize: 'Hold placed',
  reauthorize: 'Hold re-authorized',
  released: 'Hold released',
  simulated: 'Simulated',
  captured_on_reauth_failure: 'Captured after failed re-auth',
  already_captured: 'Captured in Stripe',
  already_released: 'Already released',
  adopted_existing_hold: 'Adopted existing hold',
  unresolved_deferred: 'Deferred (unresolved)',
  reauthorize_outcome_unknown: 'Re-auth outcome unknown',
  no_customer_found: 'No Stripe customer',
  ambiguous_match: 'Ambiguous customer match',
  no_payment_method_found: 'No saved card',
  in_progress: 'Skipped (in progress)',
  no_action: 'No action needed',
  nothing_to_release: 'Nothing to release',
  failed: 'Failed',
  'charge.failed': 'Charge failed',
  'invoice.payment_failed': 'Invoice payment failed',
  'payment_intent.succeeded': 'Payment succeeded',
  'payment_intent.canceled': 'Payment canceled',
  'payment_intent.payment_failed': 'Payment failed',
  'payment_intent.amount_capturable_updated': 'Hold authorized',
}

const LEVEL_VARIANT: Record<string, BadgeProps['variant']> = {
  info: 'secondary',
  warn: 'warning',
  error: 'destructive',
}

/** The filterable actions, grouped so the dropdown isn't a wall of verbs. */
const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'authorize', label: 'Hold placed' },
  { value: 'reauthorize', label: 'Hold re-authorized' },
  { value: 'released', label: 'Hold released' },
  { value: 'already_captured', label: 'Captured in Stripe' },
  { value: 'captured_on_reauth_failure', label: 'Captured after failed re-auth' },
  { value: 'no_customer_found', label: 'No Stripe customer' },
  { value: 'no_action', label: 'No action needed' },
]

const selectClass =
  'h-8 rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2'

function actionLabel(log: StripeLog): string {
  return ACTION_LABELS[log.action] ?? log.action
}

/**
 * Time bounds per result set, so paging can't move them underneath itself.
 *
 * Module scope, not a ref, and keyed by query rather than held in one slot.
 * Each of those was its own bug: a single slot loses A's bounds when B loads,
 * and a component-local ref is recreated when the route unmounts while the
 * app-wide query cache survives. Either way "Load more" re-resolves a later
 * lower bound and rows covered by the cached cursor age out.
 *
 * Lifetime is tied to the query cache rather than to a cap of its own. An
 * independent limit - however it evicts - can drop bounds for a set React Query
 * will still serve fresh, which reintroduces the same bug: cache reuse never
 * calls back in here, so a set can be the most recently *viewed* and still the
 * oldest entry. Entries are pruned only when their query has actually left the
 * cache.
 */
const boundsByKey = new Map<string, { from?: string; to?: string }>()

/**
 * Bounds a refetch is using but that are not yet in force.
 *
 * A refetch of a paged set re-resolves the window once, uses it for every page
 * of that refetch, and only replaces the committed bound when the whole thing
 * succeeds. Publishing earlier - after page 1, or before the request - would
 * leave the surviving cached cursor chain paired with a window it was never
 * built on, which is how rows go missing. Pinning the bound forever avoided
 * that but stopped relative ranges rolling, so a long-lived view kept querying
 * from its original date while the control still said "Last 30 days".
 */
const pendingBoundsByKey = new Map<
  string,
  { bounds: { from?: string; to?: string }; seenUpdatedAt: number }
>()

/**
 * Drop bounds whose query is no longer cached, so neither map grows unbounded.
 *
 * Pending entries are pruned too. A request abandoned by a filter change or a
 * route exit can settle after nothing is observing its key, so its pending
 * entry is never published or deleted by the promotion effect - and once the
 * query is garbage-collected, nothing else would ever look at it again.
 */
function pruneBounds(liveKeys: Set<string>) {
  for (const key of boundsByKey.keys()) {
    if (!liveKeys.has(key)) boundsByKey.delete(key)
  }
  for (const key of pendingBoundsByKey.keys()) {
    if (!liveKeys.has(key)) pendingBoundsByKey.delete(key)
  }
}

/**
 * The Stripe activity feed. Since CAP-651 it renders as the Protection page's
 * Activity tab rather than a page of its own; `/stripe-logs` redirects there.
 */
export function StripeLogsPage() {
  const [propertyId, setPropertyId] = useState('')
  const [action, setAction] = useState('')
  const [level, setLevel] = useState('')
  const [mode, setMode] = useState('')
  // Defaults to the last 30 days rather than everything: the log is retained
  // for 6 months, and an unbounded default meant the first page was whatever
  // happened to be newest with no sense of the window being looked at.
  const [timeRange, setTimeRange] = useState<TimeRange>(DEFAULT_TIME_RANGE)

  const queryClient = useQueryClient()
  const { data: propertiesData } = useProperties()
  const properties = useMemo(
    () => (propertiesData as { properties?: unknown[] } | undefined)?.properties ?? [],
    [propertiesData]
  )

  const filters = useMemo(
    () => ({
      propertyId: propertyId || undefined,
      action: action || undefined,
      level: level || undefined,
      mode: mode || undefined,
      limit: 100,
    }),
    [propertyId, action, level, mode]
  )

  // The time range stays SEMANTIC here ("last 30 days"), and is resolved to
  // instants inside the query function below. Resolving it in this memo would
  // freeze the boundary at render: the memo's dependencies don't change as time
  // passes, so every later refetch - on focus, on retry, on paging - would keep
  // requesting the window computed when the filters last changed. That is the
  // exact staleness the relative range exists to avoid.

  const queryKey = useMemo(() => ['stripe-logs', filters, timeRange] as const, [filters, timeRange])

  // Infinite rather than a single fetch: the endpoint pages with a cursor, and
  // a plain query would cap the view at one page and make older activity
  // unreachable. Changing a filter changes the query key, which resets paging.
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    dataUpdatedAt,
  } = useInfiniteQuery({
    queryKey,
    // Resolved once per RESULT SET, not per render and not per page.
    //
    // Per render would freeze the boundary at the moment the filters last
    // changed, so a rolling range would stop rolling. Per page is worse in a
    // subtler way: "Load more" would re-resolve `from` and advance the lower
    // bound, silently dropping records that were inside the window the first
    // page's cursor was issued against - they age past the new boundary and
    // become unreachable through the pagination chain.
    //
    // So the first page resolves and remembers; later pages reuse it, and the
    // memory is keyed by the query so any change to the filters or range
    // starts a fresh set with a fresh boundary.
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const key = JSON.stringify(queryKey)

      if (pageParam === undefined) {
        // Start of a fetch for this set: resolve the window once, and hold it
        // aside until the whole refetch succeeds.
        pendingBoundsByKey.set(key, {
          bounds: resolveTimeRange(timeRange),
          seenUpdatedAt: queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0,
        })
      }

      // Later pages of an in-flight refetch use its window; anything else uses
      // the one currently in force.
      const bounds =
        pendingBoundsByKey.get(key)?.bounds ?? boundsByKey.get(key) ?? resolveTimeRange(timeRange)

      return api.getStripeLogs({ ...filters, ...bounds, cursor: pageParam })
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { nextCursor: string | null }) => lastPage.nextCursor ?? undefined,
    staleTime: 15000,
    // An actual refresh trigger. staleTime only marks the cache stale - it
    // schedules nothing - and the app-wide client disables refetch-on-focus,
    // so a page left open never re-resolved its window: rows aged out of
    // "Last 30 days" while still on screen, and new activity never arrived.
    // Only while the view is a single page. Refetching an infinite query walks
    // every cached page sequentially, so polling a set the operator has paged
    // through would multiply requests by the number of pages opened - and
    // disable pagination for the duration of each sweep. A paged set keeps its
    // window until the filters change, which is the same trade the bound
    // pinning already makes.
    refetchInterval: query => ((query.state.data?.pages?.length ?? 1) > 1 ? false : 60_000),
    // Through the background too, so a tab returned to is current rather than
    // showing a window that quietly aged.
    refetchIntervalInBackground: true,
  })

  // Publish a pending window only once the complete refetch has landed. A
  // successful fetch is the only thing that advances dataUpdatedAt, so a
  // refetch that failed on any page leaves the previous window - and therefore
  // the surviving cursor chain - untouched. Written from an effect because it
  // syncs an external store; it sets no React state, so it cannot cascade.
  useEffect(() => {
    if (isFetching) return
    const key = JSON.stringify(queryKey)
    const pending = pendingBoundsByKey.get(key)
    if (!pending) return
    if (dataUpdatedAt <= pending.seenUpdatedAt) {
      // Fetching stopped without the data advancing: the refetch failed. Drop
      // the candidate window, or the surviving cursor chain would be paged
      // against a window its own result set never used - the very thing
      // holding it back was meant to prevent.
      pendingBoundsByKey.delete(key)
      return
    }

    {
      boundsByKey.set(key, pending.bounds)
      pendingBoundsByKey.delete(key)
      pruneBounds(
        new Set(
          queryClient
            .getQueryCache()
            .getAll()
            .map(q => JSON.stringify(q.queryKey))
        )
      )
    }
  }, [queryClient, queryKey, isFetching, dataUpdatedAt])

  // A paged view is a deliberate snapshot, so it does not auto-refresh: polling
  // it would refetch every page it has open, and silently collapsing it back to
  // page one while someone is reading page three is worse than being stale.
  // Instead the staleness is stated and refreshing is theirs to trigger.
  const isPagedView = (data?.pages?.length ?? 0) > 1

  const refreshFromTop = () => {
    // Deliberately does NOT drop the committed window first. The refetch
    // resolves a fresh one into the pending slot anyway, and publish-on-success
    // swaps it in - so a refresh that fails leaves the surviving first page and
    // its cursor on the window they were actually built against. Clearing it up
    // front handed that cursor a newly resolved bound, which is the skip this
    // whole mechanism exists to prevent.
    queryClient.setQueryData(
      queryKey,
      (old: { pages: unknown[]; pageParams: unknown[] } | undefined) =>
        old ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) } : old
    )
    refetch()
  }

  const logs: StripeLog[] = useMemo(() => (data?.pages ?? []).flatMap(page => page.logs), [data])
  // Two different questions, deliberately not the same boolean.
  const hasFieldFilters = Boolean(propertyId || action || level || mode)
  // Does the query actually bound the time window? "Everything" does not, even
  // though it isn't the default.
  const hasDateBound =
    timeRange.kind === 'absolute'
      ? Boolean(timeRange.from || timeRange.to)
      : timeRange.days !== null

  // Drives the Clear button: the default range isn't something to clear.
  const hasClearableFilters = hasFieldFilters || !isDefaultTimeRange(timeRange)
  // Drives the empty state. "No Stripe activity yet" is a claim about the whole
  // history, so it is only honest when nothing bounds the query - the default
  // 30-day window very much does. Otherwise an account with older retained logs
  // but nothing recent was told no history existed at all.
  const hasActiveConstraints = hasFieldFilters || hasDateBound

  const clearFilters = () => {
    setPropertyId('')
    setAction('')
    setLevel('')
    setMode('')
    setTimeRange(DEFAULT_TIME_RANGE)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Stripe activity</CardTitle>
        <CardDescription>
          Everything this system does with Stripe - deposit holds placed, re-authorized, captured
          and released, plus failures and incoming webhook events. Newest first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Property</span>
            <select
              value={propertyId}
              onChange={e => setPropertyId(e.target.value)}
              className={selectClass}
            >
              <option value="">All properties</option>
              {(properties as Array<{ id?: string }>).map(p => (
                <option key={p.id} value={p.id}>
                  {getPropertyDisplayName(p as never, p.id ?? '')}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Action</span>
            <select
              value={action}
              onChange={e => setAction(e.target.value)}
              className={selectClass}
            >
              {ACTION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Level</span>
            <select value={level} onChange={e => setLevel(e.target.value)} className={selectClass}>
              <option value="">All levels</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              {/* The failure filter. Deliberately here and not under Action:
                  failures arrive with several different action values
                  (`failed`, `charge.failed`, `payment_intent.payment_failed`),
                  so a single action option could only ever match some of
                  them. Level is the dimension that covers all of them. */}
              <option value="error">Error (all failures)</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Mode</span>
            {/* Simulated is a property of the run, not an action: a dry-run row
                usually carries an ordinary verb like "authorize", so filtering
                on action=simulated would hide most of them. */}
            <select value={mode} onChange={e => setMode(e.target.value)} className={selectClass}>
              <option value="">Live and simulated</option>
              <option value="live">Live only</option>
              <option value="dry_run">Simulated only</option>
            </select>
          </label>

          <TimeRangePicker value={timeRange} onChange={setTimeRange} />

          {hasClearableFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-textMuted">
              Clear filters
            </Button>
          )}
        </div>

        {isLoading ? (
          <LoadingSkeleton className="h-40 w-full" />
        ) : error && logs.length === 0 ? (
          <ErrorState error={error as Error} onRetry={refetch} />
        ) : logs.length === 0 ? (
          <EmptyState
            message={
              hasActiveConstraints
                ? 'No matching activity — try widening the date range or clearing a filter.'
                : 'No Stripe activity yet. Deposit activity appears here once the hourly sweep runs.'
            }
          />
        ) : (
          <>
            {error && (
              // A recurring poll turns any transient failure into a blanked
              // table if the error branch wins outright. The rows are still
              // usable - they are simply not current - so they stay, with the
              // staleness stated rather than implied.
              <p className="mb-3 text-sm text-warning">
                Couldn&apos;t refresh just now, so this may be out of date.{' '}
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="underline underline-offset-2"
                >
                  Try again
                </button>
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap text-sm text-textMuted">
                      {formatLocaleDateTime(log.occurred_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={LEVEL_VARIANT[log.level] ?? 'secondary'}>
                          {actionLabel(log)}
                        </Badge>
                        {/* Without this a simulated decision would be
                          indistinguishable from a real hold. */}
                        {log.mode === 'dry_run' && <Badge variant="secondary">Simulated</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {log.property_name || log.property_id || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-textMuted">
                      {log.customer_id ? (
                        <a
                          href={`https://dashboard.stripe.com/customers/${log.customer_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {log.customer_email || log.customer_id}
                        </a>
                      ) : (
                        log.customer_email || '—'
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatLogAmount(log.amount_cents, log.currency)}
                    </TableCell>
                    <TableCell className="text-sm text-textMuted">{log.message || '—'}</TableCell>
                    <TableCell>
                      {log.payment_intent_id && !log.payment_intent_id.startsWith('simulated-') ? (
                        <a
                          href={`https://dashboard.stripe.com/payments/${log.payment_intent_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-mint hover:underline"
                        >
                          Stripe
                          <ExternalLink className="h-3 w-3" aria-hidden="true" />
                        </a>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {isPagedView && (
          <p className="mt-4 text-center text-xs text-textMuted">
            Auto-refresh is paused while older pages are open.{' '}
            <button
              type="button"
              onClick={refreshFromTop}
              disabled={isFetching || isFetchingNextPage}
              className="underline underline-offset-2 disabled:opacity-50"
            >
              Refresh from the top
            </button>
          </p>
        )}

        {hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchNextPage()}
              // Disabled during ANY fetch, not just paging. A stale multi-page
              // query remounting starts a background first-page refetch while
              // still showing the cached pages and hasNextPage - paging then
              // would send the old cached cursor against the newly resolved
              // bound, which can skip records.
              disabled={isFetching || isFetchingNextPage}
            >
              {isFetching || isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
